import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { navigation } from '../config/navigation';
import { buildNavigation } from '../config/navigation';
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  UserIcon,
  UserGroupIcon,
  DocumentTextIcon,
  AdjustmentsHorizontalIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import ComposeEmailDialog from './ComposeEmailDialog';

const TopNavigation = () => {
  const { user, logout, checkPermission, hasAnyPermission, userPermissions } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [adminSettingsDropdownOpen, setAdminSettingsDropdownOpen] = useState(false);
  const [otherApplicationsDropdownOpen, setOtherApplicationsDropdownOpen] = useState(false);
  const [newMenuDropdownOpen, setNewMenuDropdownOpen] = useState(false);
  const [newMenuSubmenuOpen, setNewMenuSubmenuOpen] = useState(null); // Track which submenu is open
  const [viewSearchDropdownOpen, setViewSearchDropdownOpen] = useState(false);
  const [viewSearchSubmenuOpen, setViewSearchSubmenuOpen] = useState(null); // Track which submenu is open in View & Search
  const [apiDataDropdownOpen, setApiDataDropdownOpen] = useState(false);
  const [generatedPages, setGeneratedPages] = useState([]);
  const [generatedPagesDropdownOpen, setGeneratedPagesDropdownOpen] = useState(false);
  const [builtNavigation, setBuiltNavigation] = useState([]);
  const [navigationLoaded, setNavigationLoaded] = useState(false);
  const [isComposeEmailOpen, setIsComposeEmailOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Fetch generated pages for the menu and build navigation
  useEffect(() => {
    const fetchPages = async () => {
      const nav = await buildNavigation();
      setBuiltNavigation(nav);
      setNavigationLoaded(true);
      
      const adminItem = nav.find(item => item.name === 'Admin');
      const adminSettings = adminItem?.children?.find(child => child.name === 'Admin Settings');
      const generatedPagesMenu = adminSettings?.children?.find(child => child.name === 'Other Applications');
      // Flatten all category groups into a single array of pages
      let pages = [];
      if (generatedPagesMenu && Array.isArray(generatedPagesMenu.children)) {
        generatedPagesMenu.children.forEach(categoryGroup => {
          if (categoryGroup.children) {
            pages = pages.concat(categoryGroup.children);
          }
        });
      }
      setGeneratedPages(pages);
    };
    fetchPages();
  }, []);

  const hasAccess = (item) => {
    // Check single permission
    if (item.requiredPermission) {
      return checkPermission(item.requiredPermission);
    }

    // Check multiple permissions (any of them)
    if (item.requiredPermissions && Array.isArray(item.requiredPermissions)) {
      return hasAnyPermission(item.requiredPermissions);
    }

    // If no permission requirement, allow access
    return true;
  };

  const filteredNavigation = navigationLoaded ? builtNavigation.filter(item => hasAccess(item)) : [];

  // Process Other Applications menu at top level
  const processOtherApplicationsMenu = (item) => {
    if (item.name === 'Other Applications' && item._groupedPages) {
      const hasCustomPageSubmissionsPermission = checkPermission('View Custom Page Submissions');
      
      if (hasCustomPageSubmissionsPermission) {
        console.log('TopNavigation - Processing Other Applications menu at top level');
        const otherApplicationsItem = { ...item };
        otherApplicationsItem.children = [];
        
        // Add category groups as submenus with submission links
        Object.entries(item._groupedPages).forEach(([category, submissionPages]) => {
          if (submissionPages.length > 0) {
            otherApplicationsItem.children.push({
              name: category,
              type: 'category',
              children: submissionPages.map(submissionPage => ({
                name: submissionPage.name,
                path: submissionPage.path,
                element: submissionPage.element,
                requiredPermission: submissionPage.requiredPermission,
                pageId: submissionPage.pageId,
                pageTitle: submissionPage.pageTitle
              }))
            });
          }
        });
        
        return otherApplicationsItem;
      }
    }
    return item;
  };

  // Process navigation items before rendering
  let processedNavigation = filteredNavigation
    .map(item => processOtherApplicationsMenu(item))
    .filter(item => item.name !== 'Other Applications'); // Hide Other Applications menu

  // Debug logging
  console.log('TopNavigation - User permissions check:');
  console.log('View Custom Page Submissions permission:', checkPermission('View Custom Page Submissions'));
  console.log('View Admin Settings permission:', checkPermission('View Admin Settings'));
  console.log('Admin menu in filteredNavigation:', filteredNavigation.find(item => item.name === 'Admin'));
  console.log('Admin menu in builtNavigation:', builtNavigation.find(item => item.name === 'Admin'));
  console.log('Navigation loaded:', navigationLoaded);
  console.log('Built navigation length:', builtNavigation.length);
  console.log('Filtered navigation length:', filteredNavigation.length);
  console.log('All items in filteredNavigation:', filteredNavigation.map(item => item.name));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Get Admin submenu items
  const getAdminSubmenuItems = () => {
    const adminItem = filteredNavigation.find(item => item.name === 'Admin');
    const items = adminItem?.children || [];
    
    console.log('TopNavigation - Admin submenu debug:');
    console.log('Admin item found:', !!adminItem);
    console.log('Items in Admin:', items.map(item => item.name));
    
    // Return items as-is since Other Applications is now top-level
    return items;
  };

  // Get Admin Settings submenu items
  const getAdminSettingsSubmenuItems = () => {
    const adminItem = filteredNavigation.find(item => item.name === 'Admin');
    const adminSettingsItem = adminItem?.children?.find(child => child.name === 'Admin Settings');
    const items = adminSettingsItem?.children || [];
    
    console.log('TopNavigation - Admin Settings submenu debug:');
    console.log('Admin item found:', !!adminItem);
    console.log('Admin Settings item found:', !!adminSettingsItem);
    console.log('Items in Admin Settings:', items.map(item => item.name));
    
    // Return items as-is since Other Applications is now top-level
    return items;
  };

  const adminSubmenuItems = getAdminSubmenuItems();
  const adminSettingsSubmenuItems = getAdminSettingsSubmenuItems();

  // Filter Admin Settings submenu items first
  const filteredAdminSettingsSubmenuItems = adminSettingsSubmenuItems.filter(item =>
    item.requiredPermission ? checkPermission(item.requiredPermission) : true
  );

  // Filter items based on permissions and hide Other Applications
  const filteredAdminSubmenuItems = adminSubmenuItems
    .filter(item =>
      item.requiredPermission ? checkPermission(item.requiredPermission) : true
    )
    .filter(item => item.name !== 'Other Applications') // Hide Other Applications menu
    .filter(item => {
      // Hide Admin Settings if it has no submenus
      if (item.name === 'Admin Settings') {
        return filteredAdminSettingsSubmenuItems.length > 0;
      }
      return true;
    });

  // Hide Admin menu if it has no child menus
  processedNavigation = processedNavigation.filter(item => {
    if (item.name === 'Admin') {
      return filteredAdminSubmenuItems.length > 0;
    }
    return true;
  });

  // Debug logging for filtered admin settings submenu items
  console.log('TopNavigation - Filtered Admin Settings submenu items:', filteredAdminSettingsSubmenuItems.map(item => item.name));

  return (
    <nav className="bg-white shadow-soft border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Desktop Navigation */}
          <div className="flex items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/">
                <img
                  src={process.env.PUBLIC_URL + '/logo.svg'}
                  alt="Data Manager Logo"
                  className="h-10 w-auto"
                  style={{ maxHeight: '40px' }}
                  draggable="false"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:ml-6 md:flex md:space-x-2">
              {processedNavigation.map((item) => {
                // Special handling for New menu with dropdown
                if (item.name === 'New' && item.children && item.children.length > 0) {
                  return (
                    <div key={item.href} className="relative">
                      <button style={{ height: '100%' }}
                        onClick={() => setNewMenuDropdownOpen(!newMenuDropdownOpen)}
                        className={`${isActive(item.href)
                            ? 'border-indigo-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                          } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                      >
                        {item.iconComponent && typeof item.iconComponent === 'function' ? (
                          <item.iconComponent
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                          />
                        ) : (
                          <svg
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        )}
                        {item.name}
                        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${newMenuDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {newMenuDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                          <div className="py-1" role="menu" aria-orientation="vertical">
                            {item.children.map((subItem) => {
                              // Handle submenu items (like Custom Pages)
                              if (subItem.type === 'submenu' && subItem.children) {
                                const isSubmenuOpen = newMenuSubmenuOpen === subItem.name;
                                return (
                                  <div 
                                    key={subItem.name} 
                                    className="relative" 
                                    onMouseEnter={() => setNewMenuSubmenuOpen(subItem.name)} 
                                    onMouseLeave={() => setNewMenuSubmenuOpen(null)}
                                  >
                                    <div className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200">
                                      <div className="flex items-center">
                                        {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                          <subItem.iconComponent className="mr-3 h-4 w-4" />
                                        )}
                                        {subItem.name}
                                      </div>
                                      <ChevronDownIcon className="h-4 w-4 rotate-[-90deg]" />
                                    </div>
                                    {isSubmenuOpen && (
                                      <div className="absolute left-full top-0 ml-1 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                                        <div className="py-1">
                                          {subItem.children.map((categoryItem) => (
                                            <div key={categoryItem.name} className="relative">
                                              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                {categoryItem.name}
                                              </div>
                                              {categoryItem.children && categoryItem.children.map((categoryChild) => {
                                                if (categoryChild.requiredPermission && !checkPermission(categoryChild.requiredPermission)) {
                                                  return null;
                                                }
                                                const linkPath = categoryChild.customPageId 
                                                  ? `/custom-page/${categoryChild.customPageId}`
                                                  : `/${categoryChild.path}`;
                                                return (
                                                  <Link
                                                    key={categoryChild.path || categoryChild.customPageId}
                                                    to={linkPath}
                                                    className={`${location.pathname === linkPath
                                                        ? 'bg-indigo-50 text-indigo-700'
                                                        : 'text-gray-700 hover:bg-gray-50'
                                                      } flex items-center px-6 py-2 text-sm transition-all duration-200 hover:shadow-sm`}
                                                    onClick={() => {
                                                      setNewMenuDropdownOpen(false);
                                                      setNewMenuSubmenuOpen(null);
                                                    }}
                                                  >
                                                    {categoryChild.iconComponent && typeof categoryChild.iconComponent === 'function' && (
                                                      <categoryChild.iconComponent className="mr-3 h-4 w-4" />
                                                    )}
                                                    {categoryChild.name}
                                                  </Link>
                                                );
                                              })}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              // Handle category groups (direct categories in New menu - legacy support)
                              if (subItem.type === 'category' && subItem.children) {
                                return (
                                  <div key={subItem.name} className="relative">
                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                      {subItem.name}
                                    </div>
                                    {subItem.children.map((categoryChild) => {
                                      if (categoryChild.requiredPermission && !checkPermission(categoryChild.requiredPermission)) {
                                        return null;
                                      }
                                      const linkPath = categoryChild.customPageId 
                                        ? `/custom-page/${categoryChild.customPageId}`
                                        : `/${categoryChild.path}`;
                                      return (
                                        <Link
                                          key={categoryChild.path || categoryChild.customPageId}
                                          to={linkPath}
                                          className={`${location.pathname === linkPath
                                              ? 'bg-indigo-50 text-indigo-700'
                                              : 'text-gray-700 hover:bg-gray-50'
                                            } flex items-center px-6 py-2 text-sm transition-all duration-200 hover:shadow-sm`}
                                          onClick={() => setNewMenuDropdownOpen(false)}
                                        >
                                          {categoryChild.iconComponent && typeof categoryChild.iconComponent === 'function' && (
                                            <categoryChild.iconComponent className="mr-3 h-4 w-4" />
                                          )}
                                          {categoryChild.name}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                );
                              }
                              // Handle regular menu items
                              if (subItem.requiredPermission && !checkPermission(subItem.requiredPermission)) {
                                return null;
                              }
                              return (
                                <Link
                                  key={subItem.path}
                                  to={`/${subItem.path}`}
                                  className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200 hover:shadow-sm"
                                  onClick={() => setNewMenuDropdownOpen(false)}
                                >
                                  {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                    <subItem.iconComponent className="mr-3 h-4 w-4" />
                                  )}
                                  {subItem.name}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                // Special handling for View & Search menu with dropdown
                if (item.name === 'View & Search' && item.children && item.children.length > 0) {
                  return (
                    <div key={item.href} className="relative">
                      <button style={{ height: '100%' }}
                        onClick={() => setViewSearchDropdownOpen(!viewSearchDropdownOpen)}
                        className={`${isActive(item.href)
                            ? 'border-indigo-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                          } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                      >
                        {item.iconComponent && typeof item.iconComponent === 'function' ? (
                          <item.iconComponent
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                          />
                        ) : (
                          <svg
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        )}
                        {item.name}
                        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${viewSearchDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {viewSearchDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                          <div className="py-1" role="menu" aria-orientation="vertical">
                            {item.children.map((subItem) => {
                              // Handle submenu items (like Custom Pages)
                              if (subItem.type === 'submenu' && subItem.children) {
                                const isSubmenuOpen = viewSearchSubmenuOpen === subItem.name;
                                return (
                                  <div 
                                    key={subItem.name} 
                                    className="relative" 
                                    onMouseEnter={() => setViewSearchSubmenuOpen(subItem.name)} 
                                    onMouseLeave={() => setViewSearchSubmenuOpen(null)}
                                  >
                                    <div className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200">
                                      <div className="flex items-center">
                                        {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                          <subItem.iconComponent className="mr-3 h-4 w-4" />
                                        )}
                                        {subItem.name}
                                      </div>
                                      <ChevronDownIcon className="h-4 w-4 rotate-[-90deg]" />
                                    </div>
                                    {isSubmenuOpen && (
                                      <div className="absolute left-full top-0 ml-1 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                                        <div className="py-1">
                                          {subItem.children.map((categoryItem) => (
                                            <div key={categoryItem.name} className="relative">
                                              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                {categoryItem.name}
                                              </div>
                                              {categoryItem.children && categoryItem.children.map((categoryChild) => {
                                                if (categoryChild.requiredPermission && !checkPermission(categoryChild.requiredPermission)) {
                                                  return null;
                                                }
                                                // For View & Search, link to submissions page
                                                const linkPath = categoryChild.pageId 
                                                  ? `/admin/custom-page-submissions/${categoryChild.pageId}`
                                                  : `/admin/settings/${categoryChild.path}`;
                                                return (
                                                  <Link
                                                    key={categoryChild.path || categoryChild.pageId}
                                                    to={linkPath}
                                                    className={`${location.pathname === linkPath
                                                        ? 'bg-indigo-50 text-indigo-700'
                                                        : 'text-gray-700 hover:bg-gray-50'
                                                      } flex items-center px-6 py-2 text-sm transition-all duration-200 hover:shadow-sm`}
                                                    onClick={() => {
                                                      setViewSearchDropdownOpen(false);
                                                      setViewSearchSubmenuOpen(null);
                                                    }}
                                                  >
                                                    {categoryChild.iconComponent && typeof categoryChild.iconComponent === 'function' && (
                                                      <categoryChild.iconComponent className="mr-3 h-4 w-4" />
                                                    )}
                                                    {categoryChild.name}
                                                  </Link>
                                                );
                                              })}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              // Handle category groups (direct categories - legacy support)
                              if (subItem.type === 'category' && subItem.children) {
                                return (
                                  <div key={subItem.name} className="relative">
                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                      {subItem.name}
                                    </div>
                                    {subItem.children.map((categoryChild) => {
                                      if (categoryChild.requiredPermission && !checkPermission(categoryChild.requiredPermission)) {
                                        return null;
                                      }
                                      const linkPath = categoryChild.pageId 
                                        ? `/admin/custom-page-submissions/${categoryChild.pageId}`
                                        : `/admin/settings/${categoryChild.path}`;
                                      return (
                                        <Link
                                          key={categoryChild.path || categoryChild.pageId}
                                          to={linkPath}
                                          className={`${location.pathname === linkPath
                                              ? 'bg-indigo-50 text-indigo-700'
                                              : 'text-gray-700 hover:bg-gray-50'
                                            } flex items-center px-6 py-2 text-sm transition-all duration-200 hover:shadow-sm`}
                                          onClick={() => setViewSearchDropdownOpen(false)}
                                        >
                                          {categoryChild.iconComponent && typeof categoryChild.iconComponent === 'function' && (
                                            <categoryChild.iconComponent className="mr-3 h-4 w-4" />
                                          )}
                                          {categoryChild.name}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                );
                              }
                              // Handle regular menu items
                              if (subItem.requiredPermission && !checkPermission(subItem.requiredPermission)) {
                                return null;
                              }
                              return (
                                <Link
                                  key={subItem.path}
                                  to={`/${subItem.path}`}
                                  className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200 hover:shadow-sm"
                                  onClick={() => setViewSearchDropdownOpen(false)}
                                >
                                  {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                    <subItem.iconComponent className="mr-3 h-4 w-4" />
                                  )}
                                  {subItem.name}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                // Special handling for API Data menu with dropdown
                if (item.name === 'API Data' && item.children && item.children.length > 0) {
                  return (
                    <div key={item.name} className="relative">
                      <button
                        onClick={() => setApiDataDropdownOpen(!apiDataDropdownOpen)}
                        className={`${location.pathname.startsWith('/api-data')
                            ? 'border-indigo-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                          } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                      >
                        {item.iconComponent && typeof item.iconComponent === 'function' ? (
                          <item.iconComponent
                            className={`${location.pathname.startsWith('/api-data') ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                          />
                        ) : (
                          <svg
                            className={`${location.pathname.startsWith('/api-data') ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        )}
                        {item.name}
                        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${apiDataDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {apiDataDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                          <div className="py-1" role="menu" aria-orientation="vertical">
                            {item.children.map((subItem) => {
                              if (subItem.requiredPermission && !checkPermission(subItem.requiredPermission)) {
                                return null;
                              }
                              return (
                                <Link
                                  key={subItem.path}
                                  to={subItem.path.startsWith('/') ? subItem.path : `/${subItem.path}`}
                                  className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200 hover:shadow-sm"
                                  onClick={() => setApiDataDropdownOpen(false)}
                                >
                                  {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                    <subItem.iconComponent className="mr-3 h-4 w-4" />
                                  )}
                                  {subItem.name}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                // Special handling for Other Applications with dropdown
                if (item.name === 'Other Applications' && item.children && item.children.length > 0) {
                  return (
                    <div key={item.name} className="relative">
                      <button
                        onClick={() => setOtherApplicationsDropdownOpen(!otherApplicationsDropdownOpen)}
                        className={`${location.pathname.startsWith('/admin/custom-page-submissions')
                            ? 'border-indigo-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                          } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                      >
                        {item.iconComponent && typeof item.iconComponent === 'function' ? (
                          <item.iconComponent
                            className={`${location.pathname.startsWith('/admin/custom-page-submissions') ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                          />
                        ) : (
                          <svg
                            className={`${location.pathname.startsWith('/admin/custom-page-submissions') ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        )}
                        {item.name}
                        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${otherApplicationsDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {otherApplicationsDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                          <div className="py-1" role="menu" aria-orientation="vertical">
                            {item.children.map((categoryItem) => (
                              <div key={categoryItem.name} className="relative">
                                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  {categoryItem.name}
                                </div>
                                {categoryItem.children && categoryItem.children.map((submissionItem) => (
                                  <Link
                                    key={submissionItem.path}
                                    to={`/admin/${submissionItem.path}`}
                                    className={`${location.pathname === `/admin/${submissionItem.path}`
                                        ? 'bg-indigo-50 text-indigo-700'
                                        : 'text-gray-700 hover:bg-gray-50'
                                      } flex items-center px-6 py-2 text-sm transition-all duration-200 hover:shadow-sm`}
                                    onClick={() => setOtherApplicationsDropdownOpen(false)}
                                  >
                                    {submissionItem.name}
                                  </Link>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                // Special handling for Admin with dropdown
                if (item.name === 'Admin' && filteredAdminSubmenuItems.length > 0) {
                  return (
                    <div key={item.href} className="relative">
                      <button style={{ height: '100%' }}
                        onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                        className={`${isActive(item.href)
                            ? 'border-indigo-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                          } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                      >
                        {item.iconComponent && typeof item.iconComponent === 'function' ? (
                          <item.iconComponent
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                          />
                        ) : (
                          <svg
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        )}
                        {item.name}
                        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${adminDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {adminDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                          <div className="py-1" role="menu" aria-orientation="vertical">
                            {filteredAdminSubmenuItems.map((subItem) => {
                              // Special handling for Admin Settings with nested dropdown
                              if (subItem.name === 'Admin Settings' && filteredAdminSettingsSubmenuItems.length > 0) {
                                return (
                                  <div key={subItem.path} className="relative">
                                    <button
                                      onClick={() => setAdminSettingsDropdownOpen(!adminSettingsDropdownOpen)}
                                      className={`${location.pathname.startsWith('/admin/settings')
                                          ? 'bg-indigo-50 text-indigo-700'
                                          : 'text-gray-700 hover:bg-gray-50'
                                        } flex items-center justify-between w-full px-4 py-3 text-sm transition-all duration-200 hover:shadow-sm`}
                                    >
                                      <div className="flex items-center">
                                        {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                          <subItem.iconComponent className="mr-3 h-4 w-4" />
                                        )}
                                        {subItem.name}
                                      </div>
                                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${adminSettingsDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Admin Settings Nested Dropdown */}
                                    {adminSettingsDropdownOpen && (
                                      <div className="absolute left-full top-0 ml-1 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                                        <div className="py-1">
                                          {filteredAdminSettingsSubmenuItems.map((settingsItem) => (
                                            <Link
                                              key={settingsItem.path}
                                              to={`/admin/settings/${settingsItem.path}`}
                                              className={`${location.pathname === `/admin/settings/${settingsItem.path}`
                                                  ? 'bg-indigo-50 text-indigo-700'
                                                  : 'text-gray-700 hover:bg-gray-50'
                                                } flex items-center px-4 py-3 text-sm transition-all duration-200 hover:shadow-sm`}
                                              onClick={() => {
                                                setAdminDropdownOpen(false);
                                                setAdminSettingsDropdownOpen(false);
                                              }}
                                            >
                                              {settingsItem.name}
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              // Regular admin submenu items
                              return (
                                <Link
                                  style={{ paddingBottom: '10px !important' }}
                                  key={subItem.path}
                                  to={`/admin/${subItem.path}`}
                                  className={`${location.pathname === `/admin/${subItem.path}` ||
                                      (subItem.path === 'settings' && location.pathname.startsWith('/admin/settings'))
                                      ? 'bg-indigo-50 text-indigo-700'
                                      : 'text-gray-700 hover:bg-gray-50'
                                    } flex items-center px-4 py-3 text-sm transition-all duration-200 hover:shadow-sm`}
                                  onClick={() => setAdminDropdownOpen(false)}
                                >
                                  {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                    <subItem.iconComponent className="mr-3 h-4 w-4" />
                                  )}
                                  {subItem.name}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                // Regular navigation items
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`${isActive(item.href)
                        ? 'border-indigo-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                  >
                    {item.iconComponent && typeof item.iconComponent === 'function' ? (
                      <item.iconComponent
                        className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                          } mr-2 h-5 w-5 transition-colors duration-200`}
                      />
                    ) : (
                      <svg
                        className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                          } mr-2 h-5 w-5 transition-colors duration-200`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                      </svg>
                    )}
                    {item.name}
                  </Link>
                );
              })}

              {/* User Menu */}
            </div>
          </div>

          {/* User Menu and Mobile Menu Button */}
          <div className="flex items-center">
            {/* User Menu */}
            <div className="hidden md:flex md:items-center md:space-x-3">
              {/* Email Button */}
              <button
                onClick={() => setIsComposeEmailOpen(true)}
                className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200"
                title="Compose Email"
              >
                <EnvelopeIcon className="h-6 w-6" />
              </button>
              
              {/* User avatar dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((prev) => !prev)}
                  className="flex items-center rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center shadow-sm cursor-pointer hover:opacity-90">
                    <span className="text-sm font-medium text-white">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  </div>
                  <ChevronDownIcon className={`ml-1 h-4 w-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Profile
                    </Link>
                    <button
                      type="button"
                      onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 transition-all duration-200"
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <XMarkIcon className="block h-6 w-6" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="pt-2 pb-3 space-y-1 bg-white border-t border-gray-200 shadow-soft">
            {processedNavigation.map((item) => {
              // Special handling for New menu in mobile menu
              if (item.name === 'New' && item.children && item.children.length > 0) {
                return (
                  <div key={item.href}>
                    <div className="px-3 py-2 text-base font-medium text-gray-500">
                      {item.name}
                    </div>
                    <div className="pl-6 space-y-1">
                      {item.children.map((subItem) => {
                        // Handle submenu items (like Custom Pages)
                        if (subItem.type === 'submenu' && subItem.children) {
                          return (
                            <div key={subItem.name}>
                              <div className="px-3 py-2 text-sm font-medium text-gray-400">
                                {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                  <subItem.iconComponent className="mr-2 h-4 w-4 inline" />
                                )}
                                {subItem.name}
                              </div>
                              <div className="pl-6 space-y-1">
                                {subItem.children.map((categoryItem) => (
                                  <div key={categoryItem.name}>
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                      {categoryItem.name}
                                    </div>
                                    <div className="pl-3 space-y-1">
                                      {categoryItem.children && categoryItem.children.map((categoryChild) => {
                                        if (categoryChild.requiredPermission && !checkPermission(categoryChild.requiredPermission)) {
                                          return null;
                                        }
                                        const linkPath = categoryChild.customPageId 
                                          ? `/custom-page/${categoryChild.customPageId}`
                                          : `/${categoryChild.path}`;
                                        return (
                                          <Link
                                            key={categoryChild.path || categoryChild.customPageId}
                                            to={linkPath}
                                            className={`${location.pathname === linkPath
                                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                                              } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                          >
                                            <div className="flex items-center">
                                              {categoryChild.iconComponent && typeof categoryChild.iconComponent === 'function' && (
                                                <categoryChild.iconComponent className="mr-3 h-4 w-4" />
                                              )}
                                              {categoryChild.name}
                                            </div>
                                          </Link>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        // Handle category groups (direct categories in New menu - legacy support)
                        if (subItem.type === 'category' && subItem.children) {
                          return (
                            <div key={subItem.name}>
                              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                {subItem.name}
                              </div>
                              <div className="pl-3 space-y-1">
                                {subItem.children.map((categoryChild) => {
                                  if (categoryChild.requiredPermission && !checkPermission(categoryChild.requiredPermission)) {
                                    return null;
                                  }
                                  const linkPath = categoryChild.customPageId 
                                    ? `/custom-page/${categoryChild.customPageId}`
                                    : `/${categoryChild.path}`;
                                  return (
                                    <Link
                                      key={categoryChild.path || categoryChild.customPageId}
                                      to={linkPath}
                                      className={`${location.pathname === linkPath
                                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                          : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                                        } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      <div className="flex items-center">
                                        {categoryChild.iconComponent && typeof categoryChild.iconComponent === 'function' && (
                                          <categoryChild.iconComponent className="mr-3 h-4 w-4" />
                                        )}
                                        {categoryChild.name}
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        // Handle regular menu items
                        if (subItem.requiredPermission && !checkPermission(subItem.requiredPermission)) {
                          return null;
                        }
                        return (
                          <Link
                            key={subItem.path}
                            to={`/${subItem.path}`}
                            className={`${location.pathname === `/${subItem.path}`
                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                              } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="flex items-center">
                              {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                <subItem.iconComponent className="mr-3 h-4 w-4" />
                              )}
                              {subItem.name}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              // Special handling for View & Search in mobile menu
              if (item.name === 'View & Search' && item.children && item.children.length > 0) {
                return (
                  <div key={item.href}>
                    <div className="px-3 py-2 text-base font-medium text-gray-500">
                      {item.name}
                    </div>
                    <div className="pl-6 space-y-1">
                      {item.children.map((subItem) => {
                        // Handle submenu items (like Custom Pages)
                        if (subItem.type === 'submenu' && subItem.children) {
                          return (
                            <div key={subItem.name}>
                              <div className="px-3 py-2 text-sm font-medium text-gray-400">
                                {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                  <subItem.iconComponent className="mr-2 h-4 w-4 inline" />
                                )}
                                {subItem.name}
                              </div>
                              <div className="pl-6 space-y-1">
                                {subItem.children.map((categoryItem) => (
                                  <div key={categoryItem.name}>
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                      {categoryItem.name}
                                    </div>
                                    <div className="pl-3 space-y-1">
                                      {categoryItem.children && categoryItem.children.map((categoryChild) => {
                                        if (categoryChild.requiredPermission && !checkPermission(categoryChild.requiredPermission)) {
                                          return null;
                                        }
                                        // For View & Search, link to submissions page
                                        const linkPath = categoryChild.pageId 
                                          ? `/admin/custom-page-submissions/${categoryChild.pageId}`
                                          : `/admin/settings/${categoryChild.path}`;
                                        return (
                                          <Link
                                            key={categoryChild.path || categoryChild.pageId}
                                            to={linkPath}
                                            className={`${location.pathname === linkPath
                                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                                              } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                          >
                                            <div className="flex items-center">
                                              {categoryChild.iconComponent && typeof categoryChild.iconComponent === 'function' && (
                                                <categoryChild.iconComponent className="mr-3 h-4 w-4" />
                                              )}
                                              {categoryChild.name}
                                            </div>
                                          </Link>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        // Handle category groups (direct categories - legacy support)
                        if (subItem.type === 'category' && subItem.children) {
                          return (
                            <div key={subItem.name}>
                              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                {subItem.name}
                              </div>
                              <div className="pl-3 space-y-1">
                                {subItem.children.map((categoryChild) => {
                                  if (categoryChild.requiredPermission && !checkPermission(categoryChild.requiredPermission)) {
                                    return null;
                                  }
                                  const linkPath = categoryChild.pageId 
                                    ? `/admin/custom-page-submissions/${categoryChild.pageId}`
                                    : `/admin/settings/${categoryChild.path}`;
                                  return (
                                    <Link
                                      key={categoryChild.path || categoryChild.pageId}
                                      to={linkPath}
                                      className={`${location.pathname === linkPath
                                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                          : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                                        } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      <div className="flex items-center">
                                        {categoryChild.iconComponent && typeof categoryChild.iconComponent === 'function' && (
                                          <categoryChild.iconComponent className="mr-3 h-4 w-4" />
                                        )}
                                        {categoryChild.name}
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        // Handle regular menu items
                        if (subItem.requiredPermission && !checkPermission(subItem.requiredPermission)) {
                          return null;
                        }
                        return (
                          <Link
                            key={subItem.path}
                            to={`/${subItem.path}`}
                            className={`${location.pathname === `/${subItem.path}`
                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                              } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="flex items-center">
                              {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                <subItem.iconComponent className="mr-3 h-4 w-4" />
                              )}
                              {subItem.name}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              // Special handling for Admin in mobile menu
              if (item.name === 'Admin' && filteredAdminSubmenuItems.length > 0) {
                return (
                  <div key={item.href}>
                    <div className="px-3 py-2 text-base font-medium text-gray-500">
                      {item.name}
                    </div>
                    <div className="pl-6 space-y-1">
                      {filteredAdminSubmenuItems.map((subItem) => {
                        // Special handling for Admin Settings in mobile menu
                        if (subItem.name === 'Admin Settings' && filteredAdminSettingsSubmenuItems.length > 0) {
                          return (
                            <div key={subItem.path}>
                              <div className="px-3 py-2 text-sm font-medium text-gray-400">
                                {subItem.name}
                              </div>
                              <div className="pl-6 space-y-1">
                                {filteredAdminSettingsSubmenuItems.map((settingsItem) => (
                                  <Link
                                    key={settingsItem.path}
                                    to={`/admin/settings/${settingsItem.path}`}
                                    className={`${location.pathname === `/admin/settings/${settingsItem.path}`
                                        ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                                      } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {settingsItem.name}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        // Special handling for Other Applications in mobile menu
                        if (subItem.name === 'Other Applications' && subItem.children && subItem.children.length > 0) {
                          return (
                            <div key={subItem.name}>
                              <div className="px-3 py-2 text-sm font-medium text-gray-400">
                                {subItem.name}
                              </div>
                              <div className="pl-6 space-y-1">
                                {subItem.children.map((categoryItem) => (
                                  <div key={categoryItem.name} className="relative">
                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                      {categoryItem.name}
                                    </div>
                                    {categoryItem.children && categoryItem.children.map((submissionItem) => (
                                      <Link
                                        key={submissionItem.path}
                                        to={`/admin/${submissionItem.path}`}
                                        className={`${location.pathname === `/admin/${submissionItem.path}`
                                            ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                            : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                                          } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                      >
                                        {submissionItem.name}
                                      </Link>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        // Regular admin submenu items
                        return (
                          <Link
                            key={subItem.path}
                            to={`/admin/${subItem.path}`}
                            className={`${location.pathname === `/admin/${subItem.path}` ||
                                (subItem.path === 'settings' && location.pathname.startsWith('/admin/settings'))
                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                              } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="flex items-center">
                              {subItem.iconComponent && typeof subItem.iconComponent === 'function' && (
                                <subItem.iconComponent className="mr-3 h-4 w-4" />
                              )}
                              {subItem.name}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // Regular mobile navigation items
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`${isActive(item.href)
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-200`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="flex items-center">
                    {item.iconComponent && typeof item.iconComponent === 'function' ? (
                      <item.iconComponent
                        className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                          } mr-3 h-6 w-6 transition-colors duration-200`}
                      />
                    ) : (
                      <svg
                        className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                          } mr-3 h-6 w-6 transition-colors duration-200`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                      </svg>
                    )}
                    {item.name}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Mobile user menu */}
          <div className="pt-4 pb-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between px-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center shadow-sm">
                  <span className="text-sm font-medium text-white">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setIsComposeEmailOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200"
                  title="Compose Email"
                >
                  <EnvelopeIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to="/profile"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-indigo-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all duration-200"
                >
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all duration-200"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close dropdowns */}
      {(userMenuOpen || adminDropdownOpen || adminSettingsDropdownOpen || newMenuDropdownOpen || viewSearchDropdownOpen || otherApplicationsDropdownOpen || newMenuSubmenuOpen || viewSearchSubmenuOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setUserMenuOpen(false);
            setAdminDropdownOpen(false);
            setAdminSettingsDropdownOpen(false);
            setNewMenuDropdownOpen(false);
            setNewMenuSubmenuOpen(null);
            setViewSearchDropdownOpen(false);
            setViewSearchSubmenuOpen(null);
            setOtherApplicationsDropdownOpen(false);
          }}
        />
      )}

      {/* Compose Email Dialog */}
      <ComposeEmailDialog
        isOpen={isComposeEmailOpen}
        onClose={() => setIsComposeEmailOpen(false)}
      />
    </nav>
  );
};

export default TopNavigation; 